import {availableLocales} from '@legit.health/ui';

const FALLBACK_LOCALE = 'en-US';

/**
 * Gets the list of supported locale codes from availableLocales.
 * Returns them in the format used by i18n (e.g., 'es-ES', 'en-US').
 */
function getSupportedLocales(): string[] {
  return availableLocales.map((locale) => locale.code);
}

/**
 * Inserts a locale into a URL, placing it after the domain.
 * For example:
 *   Input: https://jnj-77242113.legit.health/for-site-staff/understanding-roles/?ShowDashboard=...
 *   Locale: es-ES
 *   Output: https://jnj-77242113.legit.health/es-ES/for-site-staff/understanding-roles/?ShowDashboard=...
 *
 * If the locale is not supported, defaults to 'en-US'.
 *
 * @param baseUrl The base URL to localize
 * @param locale The locale code to use (e.g., 'es-ES', 'en-US')
 * @returns The localized URL with the locale inserted after the domain
 */
export function getLocalizedUrl(baseUrl: string, locale: string | undefined): string {
  try {
    const url = new URL(baseUrl);
    const supportedLocales = getSupportedLocales();
    const resolvedLocale = locale && supportedLocales.includes(locale) ? locale : FALLBACK_LOCALE;

    // Split the pathname to insert the locale
    const pathname = url.pathname;
    const localePrefix = `/${resolvedLocale}`;

    // If the locale is already in the pathname, don't add it again
    if (pathname.startsWith(localePrefix)) {
      return baseUrl;
    }

    // Insert the locale after the domain (before the pathname)
    url.pathname = localePrefix + pathname;

    return url.toString();
  } catch {
    // If URL parsing fails, return the original URL
    // This shouldn't happen with valid URLs, but it's a safe fallback
    return baseUrl;
  }
}
